import React, { useMemo } from 'react';
import { Tooltip, Badge } from 'evergreen-ui';
import { LessonSchedule } from '@shared/types';
import { ReactComponent as CalendarDayRegularIcon } from '../../../../assets/icons/calendar-day-regular.svg';

export const LessonScheduleBadge = ({
    schedule,
    enrollmentDate,
}: {
    schedule?: LessonSchedule;
    enrollmentDate?: number; // Unix timestamp in seconds
}) => {
    const now = useMemo(() => new Date(), []);

    const isAvailable = useMemo(() => {
        if (!schedule) return true;
        if (schedule.type === 'immediate') return true;
        if (schedule.type === 'fixed') {
            return now.getTime() > schedule.timestamp * 1000;
        }
        if (schedule.type === 'relative' && enrollmentDate) {
            const availableDate = new Date(enrollmentDate * 1000);
            availableDate.setDate(availableDate.getDate() + schedule.daysAfterEnrollment);
            return now.getTime() > availableDate.getTime();
        }
        return false;
    }, [schedule, enrollmentDate, now]);

    if (!schedule) return null;
    if (isAvailable) return null;

    switch (schedule.type) {
        case 'relative': {
            if (!enrollmentDate) return null;

            const availableDate = new Date(enrollmentDate * 1000);
            availableDate.setDate(availableDate.getDate() + schedule.daysAfterEnrollment);
            const daysUntilAvailable = Math.ceil(
                (availableDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
            );

            if (daysUntilAvailable > 0) {
                const time = availableDate.toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                });
                const date = availableDate.toLocaleDateString(undefined, {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                });

                return (
                    <Tooltip
                        content={
                            daysUntilAvailable === 0
                                ? `This lesson will become available today at ${time}`
                                : `This lesson will become available on ${date} at ${time}`
                        }
                    >
                        <Badge
                            color="blue"
                            whiteSpace="nowrap"
                            display="flex"
                            alignItems="center"
                            gap={5}
                            width="min-content"
                        >
                            <CalendarDayRegularIcon height={12} width={12} fill="var(--blue)" />
                            Available in {daysUntilAvailable}{' '}
                            {daysUntilAvailable === 1 ? 'day' : 'days'}
                        </Badge>
                    </Tooltip>
                );
            }
            return null;
        }
        case 'fixed': {
            const dueDate = new Date(schedule.timestamp * 1000);
            const date = dueDate.toLocaleDateString(undefined, {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
            });
            const time = dueDate.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
            });
            return (
                <Tooltip content={`This lesson will become available on ${date} at ${time}`}>
                    <Badge
                        color="blue"
                        whiteSpace="nowrap"
                        display="flex"
                        alignItems="center"
                        gap={5}
                        width="min-content"
                    >
                        <CalendarDayRegularIcon height={12} width={12} fill="var(--blue)" />
                        Available on {date}
                    </Badge>
                </Tooltip>
            );
        }
        case 'immediate':
        default:
            return null;
    }
};
